/******************************************************************
 POLICY LOAN CALCULATOR™ — CANADA VERSION 1.0
 Calculation Logic Spec
 ******************************************************************/
import { EPSILON, isPaidOff as isSettled } from "./money";

// ============================================================
// SECTION 1: CORE TYPES
// ============================================================

export type PaymentFrequency =
  | "weekly"
  | "biweekly"
  | "semimonthly"
  | "monthly"
  | "quarterly"
  | "semiannual"
  | "annual"
  | "endofterm";

export type Jurisdiction = "canada" | "usa";

export type DayCountConvention =
  | "actual_actual"   // use actual days in year: 365 or 366
  | "actual_365";     // always divide by 365

export type PolicyLoanPayment = {
  id: string;
  date: string;       // ISO date YYYY-MM-DD
  amount: number;     // positive amount
  source?: "manual" | "planned";
};

export type RateChangeEvent = {
  id: string;
  effectiveDate: string; // ISO date YYYY-MM-DD
  annualRate: number;    // decimal, e.g. 0.065 for 6.5%
};

export type PolicyLoanInput = {
  jurisdiction: Jurisdiction;
  loanAmount: number;
  policyLoanAnnualRate: number;              // decimal
  policyLoanEffectiveDate: string;
  policyAnniversaryMonth: number;            // 1-12
  policyAnniversaryDay: number;              // 1-31
  availableCashValue?: number | null;
  existingPolicyLoanBalance?: number;
  existingAccruedInterest?: number;
  dayCountConvention: DayCountConvention;
  payments: PolicyLoanPayment[];
  rateChanges?: RateChangeEvent[];
  projectionEndDate?: string;
};

export type StandardLoanInput = {
  principal: number;
  annualRate: number;             // decimal, e.g. 0.10 for 10%
  termCount: number;
  paymentFrequency: PaymentFrequency;
  startDate: string;
};

export type PlannedRepaymentStrategyInput = {
  borrowedAmount: number;
  targetAnnualRate: number;       // decimal
  termCount: number;
  paymentFrequency: PaymentFrequency;
  startDate: string;
  overridePaymentAmount?: number; // user's desired payment (e.g. $1000 vs solved $999.75)
};

export type PolicyLoanLedgerRow = {
  date: string;
  eventType:
    | "loan_advance"
    | "payment"
    | "capitalization"
    | "rate_change"
    | "year_end_summary"
    | "projection_end";
  description: string;
  daysInInterval: number;
  annualRateUsed: number;
  startingPrincipal: number;
  intervalInterestAccrued: number;
  accruedInterestBeforeEvent: number;
  paymentAmount: number;
  /** Portion of the payment applied to accrued-but-uncapitalized interest. */
  interestPaid: number;
  principalReduction: number;
  /** Amount of the payment that could not be applied (balance already settled). */
  unappliedPayment: number;
  capitalizedInterest: number;
  endingPrincipal: number;
  endingAccruedInterest: number;
  totalIndebtednessAfterEvent: number;
  availableCashValue?: number | null;
  lapseRiskWarning?: boolean;
};

export type PolicyLoanResult = {
  ledger: PolicyLoanLedgerRow[];
  currentPrincipalBalance: number;
  currentAccruedInterestNotCapitalized: number;
  currentTotalIndebtedness: number;
  nextPolicyAnniversaryDate: string | null;
  nextCapitalizationEstimate: number;
  totalInterestAccruedToProjectionEnd: number;
  totalInterestCapitalizedToProjectionEnd: number;
  totalPaymentsApplied: number;
  estimatedPayoffDate: string | null;
  hasLapseRiskWarning: boolean;
};

export type StandardLoanAmortizationRow = {
  paymentNumber: number;
  paymentDate: string;
  paymentAmount: number;
  interestPortion: number;
  principalPortion: number;
  remainingBalance: number;
};

export type StandardLoanResult = {
  periodicRate: number;
  paymentsPerYear: number;
  scheduledPayment: number;
  totalPayments: number;
  totalInterest: number;
  payoffDate: string;
  amortizationSchedule: StandardLoanAmortizationRow[];
};

export type FamilySystemRecaptureResult = {
  intendedPaymentPerPeriod: number;
  intendedTotalPayments: number;
  intendedTotalInterest: number;
  actualPolicyLoanInterestCost: number;
  additionalIntendedFinancingMargin: number;
  suggestedExtraToRedirectToFamilySystemPremiums: number;
  explanationText: string;
};

// ============================================================
// SECTION 2: DATE HELPERS
// ============================================================

export function parseDate(dateStr: string): Date {
  return new Date(dateStr + "T00:00:00");
}

export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

export function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

export function diffInDays(startDateStr: string, endDateStr: string): number {
  const start = parseDate(startDateStr);
  const end = parseDate(endDateStr);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
}

export function addDays(dateStr: string, days: number): string {
  const d = parseDate(dateStr);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

export function addMonths(dateStr: string, months: number): string {
  const d = parseDate(dateStr);
  const targetMonth = d.getMonth() + months;
  d.setDate(1); // avoid overflow
  d.setMonth(targetMonth);
  // Clamp to last day of target month if original day exceeds it
  const originalDay = parseDate(dateStr).getDate();
  const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(originalDay, lastDayOfMonth));
  return toISODate(d);
}

export function addYears(dateStr: string, years: number): string {
  const d = parseDate(dateStr);
  d.setFullYear(d.getFullYear() + years);
  return toISODate(d);
}

export function compareDateStr(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// ============================================================
// SECTION 3: POLICY ANNIVERSARY LOGIC
// ============================================================

export function buildAnniversaryDateForYear(
  year: number,
  anniversaryMonth: number,
  anniversaryDay: number
): string {
  let day = anniversaryDay;
  if (anniversaryMonth === 2 && anniversaryDay === 29 && !isLeapYear(year)) {
    day = 28;
  }
  const month = String(anniversaryMonth).padStart(2, "0");
  const dayStr = String(day).padStart(2, "0");
  return `${year}-${month}-${dayStr}`;
}

export function getNextPolicyAnniversaryDate(
  referenceDate: string,
  anniversaryMonth: number,
  anniversaryDay: number
): string {
  const ref = parseDate(referenceDate);
  const year = ref.getFullYear();
  const thisYearAnniv = buildAnniversaryDateForYear(year, anniversaryMonth, anniversaryDay);
  if (compareDateStr(thisYearAnniv, referenceDate) > 0) {
    return thisYearAnniv;
  }
  return buildAnniversaryDateForYear(year + 1, anniversaryMonth, anniversaryDay);
}

export function getPolicyAnniversariesBetween(
  startDate: string,
  endDate: string,
  anniversaryMonth: number,
  anniversaryDay: number
): string[] {
  const anniversaries: string[] = [];
  let next = getNextPolicyAnniversaryDate(startDate, anniversaryMonth, anniversaryDay);
  while (compareDateStr(next, endDate) <= 0) {
    anniversaries.push(next);
    next = buildAnniversaryDateForYear(
      parseDate(next).getFullYear() + 1,
      anniversaryMonth,
      anniversaryDay
    );
  }
  return anniversaries;
}

export function getYearEndDatesBetween(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  let year = parseDate(startDate).getFullYear();
  const endYear = parseDate(endDate).getFullYear();

  while (year <= endYear) {
    const dec31 = `${year}-12-31`;
    if (compareDateStr(dec31, startDate) > 0 && compareDateStr(dec31, endDate) <= 0) {
      dates.push(dec31);
    }
    year += 1;
  }

  return dates;
}

// ============================================================
// SECTION 4: DAY-COUNT / INTEREST ACCRUAL LOGIC
// ============================================================

export function computeAccruedInterestForInterval(
  principalBalance: number,
  annualRate: number,
  intervalStartDate: string,
  intervalEndDate: string,
  convention: DayCountConvention
): number {
  if (principalBalance <= 0) return 0;
  const totalDays = diffInDays(intervalStartDate, intervalEndDate);
  if (totalDays <= 0) return 0;

  if (convention === "actual_365") {
    return principalBalance * annualRate * (totalDays / 365);
  }

  // actual_actual: split by calendar year for leap year handling
  let interest = 0;
  let cursor = intervalStartDate;
  while (compareDateStr(cursor, intervalEndDate) < 0) {
    const cursorYear = parseDate(cursor).getFullYear();
    const yearEndExclusive = `${cursorYear + 1}-01-01`;
    const subPeriodEnd =
      compareDateStr(yearEndExclusive, intervalEndDate) < 0
        ? yearEndExclusive
        : intervalEndDate;
    const days = diffInDays(cursor, subPeriodEnd);
    interest += principalBalance * annualRate * (days / daysInYear(cursorYear));
    cursor = subPeriodEnd;
  }
  return interest;
}

// ============================================================
// SECTION 5: PAYMENT FREQUENCY HELPERS
// ============================================================

export function getPaymentsPerYear(frequency: PaymentFrequency): number {
  switch (frequency) {
    case "weekly": return 52;
    case "biweekly": return 26;
    case "semimonthly": return 24;
    case "monthly": return 12;
    case "quarterly": return 4;
    case "semiannual": return 2;
    case "annual": return 1;
    case "endofterm": return 1; // single payment; term count is always 1
  }
}

export function getNextPaymentDate(dateStr: string, frequency: PaymentFrequency): string {
  switch (frequency) {
    case "weekly": return addDays(dateStr, 7);
    case "biweekly": return addDays(dateStr, 14);
    case "semimonthly": {
      const d = parseDate(dateStr);
      const day = d.getDate();
      if (day <= 15) {
        const target = new Date(d.getFullYear(), d.getMonth(), day + 15);
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        if (day + 15 > lastDay) target.setDate(lastDay);
        return toISODate(target);
      } else {
        const next = parseDate(addMonths(dateStr, 1));
        next.setDate(Math.max(1, day - 15));
        return toISODate(next);
      }
    }
    case "monthly": return addMonths(dateStr, 1);
    case "quarterly": return addMonths(dateStr, 3);
    case "semiannual": return addMonths(dateStr, 6);
    case "annual": return addYears(dateStr, 1);
    case "endofterm": return addYears(dateStr, 1); // fallback; endofterm uses termCount=1
  }
}

// ============================================================
// SECTION 6: STANDARD LOAN / AMORTIZATION CALCULATOR™
// ============================================================

export function calculateStandardLoanPayment(input: StandardLoanInput): number {
  const paymentsPerYear = getPaymentsPerYear(input.paymentFrequency);
  const periodicRate = input.annualRate / paymentsPerYear;
  const n = input.termCount;
  const P = input.principal;
  if (periodicRate === 0) return P / n;
  return P * (periodicRate / (1 - Math.pow(1 + periodicRate, -n)));
}

export function buildStandardLoanAmortization(input: StandardLoanInput): StandardLoanResult {
  const paymentsPerYear = getPaymentsPerYear(input.paymentFrequency);
  const periodicRate = input.annualRate / paymentsPerYear;
  const payment = calculateStandardLoanPayment(input);

  let balance = input.principal;
  // The loan is disbursed on startDate; payment #1 falls one full period
  // later, carrying the one period of interest it is charged.
  let paymentDate = getNextPaymentDate(input.startDate, input.paymentFrequency);
  let totalInterest = 0;
  const schedule: StandardLoanAmortizationRow[] = [];

  for (let i = 1; i <= input.termCount; i++) {
    const interestPortion = balance * periodicRate;
    let principalPortion = payment - interestPortion;
    let actualPayment = payment;

    if (principalPortion > balance) {
      principalPortion = balance;
      actualPayment = interestPortion + principalPortion;
    }

    balance = Math.max(0, balance - principalPortion);
    totalInterest += interestPortion;

    schedule.push({
      paymentNumber: i,
      paymentDate,
      paymentAmount: actualPayment,
      interestPortion,
      principalPortion,
      remainingBalance: balance,
    });

    if (balance <= 0.000001) {
      return {
        periodicRate,
        paymentsPerYear,
        scheduledPayment: payment,
        totalPayments: schedule.reduce((sum, row) => sum + row.paymentAmount, 0),
        totalInterest,
        payoffDate: paymentDate,
        amortizationSchedule: schedule,
      };
    }
    paymentDate = getNextPaymentDate(paymentDate, input.paymentFrequency);
  }

  return {
    periodicRate,
    paymentsPerYear,
    scheduledPayment: payment,
    totalPayments: schedule.reduce((sum, row) => sum + row.paymentAmount, 0),
    totalInterest,
    payoffDate: schedule[schedule.length - 1]?.paymentDate ?? input.startDate,
    amortizationSchedule: schedule,
  };
}

// ============================================================
// SECTION 7: PLANNED REPAYMENT STRATEGY™
// ============================================================

export function buildPlannedRepaymentSchedule(
  input: PlannedRepaymentStrategyInput
): PolicyLoanPayment[] {
  const standardLoan = buildStandardLoanAmortization({
    principal: input.borrowedAmount,
    annualRate: input.targetAnnualRate,
    termCount: input.termCount,
    paymentFrequency: input.paymentFrequency,
    startDate: input.startDate,
  });

  const override = input.overridePaymentAmount;

  return standardLoan.amortizationSchedule.map((row) => ({
    id: `planned-${row.paymentNumber}`,
    date: row.paymentDate,
    amount: override && override > row.paymentAmount
      ? (row.remainingBalance < 0.01 ? row.paymentAmount : override)
      : row.paymentAmount,
    source: "planned" as const,
  }));
}

// ============================================================
// SECTION 8: ACTUAL POLICY LOAN CALCULATOR™
// ============================================================

type PolicyLoanEngineEvent =
  | { date: string; type: "loan_advance"; description: string; amount: number }
  | { date: string; type: "payment"; description: string; amount: number }
  | { date: string; type: "capitalization"; description: string }
  | { date: string; type: "rate_change"; description: string; annualRate: number }
  | { date: string; type: "year_end_summary"; description: string }
  | { date: string; type: "projection_end"; description: string };

function determineProjectionEndDate(input: PolicyLoanInput): string {
  if (input.projectionEndDate) return input.projectionEndDate;
  const paymentDates = input.payments.map((p) => p.date).sort();
  if (paymentDates.length > 0) {
    return paymentDates[paymentDates.length - 1];
  }
  return getNextPolicyAnniversaryDate(
    input.policyLoanEffectiveDate,
    input.policyAnniversaryMonth,
    input.policyAnniversaryDay
  );
}

function buildPolicyLoanEventList(input: PolicyLoanInput): PolicyLoanEngineEvent[] {
  const endDate = determineProjectionEndDate(input);
  const events: PolicyLoanEngineEvent[] = [];

  events.push({
    date: input.policyLoanEffectiveDate,
    type: "loan_advance",
    description: "Initial policy loan advance",
    amount: input.loanAmount,
  });

  input.payments.forEach((payment) => {
    if (
      compareDateStr(payment.date, input.policyLoanEffectiveDate) >= 0 &&
      compareDateStr(payment.date, endDate) <= 0
    ) {
      events.push({
        date: payment.date,
        type: "payment",
        description: "Policy loan repayment",
        amount: payment.amount,
      });
    }
  });

  (input.rateChanges ?? []).forEach((rc) => {
    if (
      compareDateStr(rc.effectiveDate, input.policyLoanEffectiveDate) >= 0 &&
      compareDateStr(rc.effectiveDate, endDate) <= 0
    ) {
      events.push({
        date: rc.effectiveDate,
        type: "rate_change",
        description: "Policy loan rate change",
        annualRate: rc.annualRate,
      });
    }
  });

  const anniversaries = getPolicyAnniversariesBetween(
    input.policyLoanEffectiveDate,
    endDate,
    input.policyAnniversaryMonth,
    input.policyAnniversaryDay
  );

  anniversaries.forEach((annivDate) => {
    events.push({
      date: annivDate,
      type: "capitalization",
      description: "Policy anniversary capitalization",
    });
  });

  const yearEnds = getYearEndDatesBetween(input.policyLoanEffectiveDate, endDate);
  yearEnds.forEach((date) => {
    events.push({
      date,
      type: "year_end_summary",
      description: "Dec 31 interest summary",
    });
  });

  events.push({
    date: endDate,
    type: "projection_end",
    description: "Projection end",
  });

  const priority: Record<PolicyLoanEngineEvent["type"], number> = {
    loan_advance: 1,
    rate_change: 2,
    payment: 3,
    year_end_summary: 4,
    capitalization: 5,
    projection_end: 6,
  };

  events.sort((a, b) => {
    const dateCompare = compareDateStr(a.date, b.date);
    if (dateCompare !== 0) return dateCompare;
    return priority[a.type] - priority[b.type];
  });

  return events;
}

export function calculateActualPolicyLoan(input: PolicyLoanInput): PolicyLoanResult {
  const events = buildPolicyLoanEventList(input);

  let principal = Math.max(0, input.existingPolicyLoanBalance ?? 0);
  let accruedInterest = Math.max(0, input.existingAccruedInterest ?? 0);
  let currentRate = input.policyLoanAnnualRate;
  let totalInterestAccrued = 0;
  let totalInterestCapitalized = 0;
  let totalPaymentsApplied = 0;
  let hasLapseRiskWarning = false;
  let lastEventDate = input.policyLoanEffectiveDate;
  // First ledger date where principal AND accrued interest both settled
  // below one cent — the loan's real payoff date. Reset if a later event
  // somehow re-opens a balance.
  let settledOnDate: string | null = null;

  const ledger: PolicyLoanLedgerRow[] = [];

  for (let i = 0; i < events.length; i++) {
    const event = events[i];

    const intervalInterest =
      i === 0
        ? 0
        : computeAccruedInterestForInterval(
            principal,
            currentRate,
            lastEventDate,
            event.date,
            input.dayCountConvention
          );

    accruedInterest += intervalInterest;
    totalInterestAccrued += intervalInterest;

    const startingPrincipal = principal;
    const accruedInterestBeforeEvent = accruedInterest;

    let paymentAmount = 0;
    let principalReduction = 0;
    let capitalizedInterest = 0;
    let interestPaid = 0;
    let unappliedPayment = 0;

    switch (event.type) {
      case "loan_advance":
        principal += event.amount;
        break;
      case "payment": {
        // Allocation order: accrued-but-uncapitalized interest FIRST, then
        // principal. A payment can never silently erase accrued interest.
        paymentAmount = event.amount;
        interestPaid = Math.min(paymentAmount, Math.max(0, accruedInterest));
        accruedInterest -= interestPaid;
        const afterInterest = paymentAmount - interestPaid;
        principalReduction = Math.min(afterInterest, Math.max(0, principal));
        principal -= principalReduction;
        // Overpayment is never applied and never creates a negative balance.
        unappliedPayment = afterInterest - principalReduction;
        // Floating-point dust only — NEVER a real cent.
        if (Math.abs(principal) < EPSILON) principal = 0;
        if (Math.abs(accruedInterest) < EPSILON) accruedInterest = 0;
        totalPaymentsApplied += interestPaid + principalReduction;
        break;
      }
      case "capitalization":
        capitalizedInterest = accruedInterest;
        principal += accruedInterest;
        totalInterestCapitalized += accruedInterest;
        accruedInterest = 0;
        break;
      case "rate_change":
        currentRate = event.annualRate;
        break;
      case "year_end_summary":
      case "projection_end":
        break;
    }

    const totalIndebtednessAfterEvent = principal + accruedInterest;

    let lapseRiskWarning = false;
    if (
      typeof input.availableCashValue === "number" &&
      totalIndebtednessAfterEvent > input.availableCashValue
    ) {
      lapseRiskWarning = true;
      hasLapseRiskWarning = true;
    }

    ledger.push({
      date: event.date,
      eventType: event.type,
      description: event.description,
      daysInInterval: i === 0 ? 0 : diffInDays(lastEventDate, event.date),
      annualRateUsed: currentRate,
      startingPrincipal,
      intervalInterestAccrued: intervalInterest,
      accruedInterestBeforeEvent,
      paymentAmount,
      interestPaid,
      principalReduction,
      unappliedPayment,
      capitalizedInterest,
      endingPrincipal: principal,
      endingAccruedInterest: accruedInterest,
      totalIndebtednessAfterEvent,
      availableCashValue: input.availableCashValue ?? null,
      lapseRiskWarning,
    });

    if (isSettled(principal) && isSettled(accruedInterest)) {
      if (settledOnDate === null) settledOnDate = event.date;
    } else {
      settledOnDate = null;
    }

    lastEventDate = event.date;
  }

  const projectionEnd = determineProjectionEndDate(input);
  const nextPolicyAnniversaryDate = getNextPolicyAnniversaryDate(
    projectionEnd,
    input.policyAnniversaryMonth,
    input.policyAnniversaryDay
  );

  const nextCapitalizationEstimate =
    computeAccruedInterestForInterval(
      principal,
      currentRate,
      projectionEnd,
      nextPolicyAnniversaryDate,
      input.dayCountConvention
    ) + accruedInterest;

  const currentTotalIndebtedness = principal + accruedInterest;
  // A loan is only paid off when principal AND accrued interest each round
  // below one cent — and the payoff DATE is when that actually happened in
  // the ledger, not the projection end (which made early payoffs invisible
  // in dashboard comparisons).
  const estimatedPayoffDate = settledOnDate;

  return {
    ledger,
    currentPrincipalBalance: principal,
    currentAccruedInterestNotCapitalized: accruedInterest,
    currentTotalIndebtedness,
    nextPolicyAnniversaryDate,
    nextCapitalizationEstimate,
    totalInterestAccruedToProjectionEnd: totalInterestAccrued,
    totalInterestCapitalizedToProjectionEnd: totalInterestCapitalized,
    totalPaymentsApplied,
    estimatedPayoffDate,
    hasLapseRiskWarning,
  };
}

// ============================================================
// SECTION 9: COMBINED REPAYMENT PROJECTION™
// ============================================================

export type CombinedScenarioInput = {
  /** Selects jurisdiction-specific narrative text; math is identical. */
  jurisdiction?: Jurisdiction;
  policyLoanInput: Omit<PolicyLoanInput, "payments" | "projectionEndDate">;
  repaymentStrategy: PlannedRepaymentStrategyInput;
};

export type CombinedScenarioResult = {
  plannedPayments: PolicyLoanPayment[];
  actualPolicyLoan: PolicyLoanResult;
  standardLoanComparison: StandardLoanResult;
  familySystemRecapture: FamilySystemRecaptureResult;
};

export function runCombinedScenario(input: CombinedScenarioInput): CombinedScenarioResult {
  const plannedPayments = buildPlannedRepaymentSchedule(input.repaymentStrategy);

  const projectionEndDate =
    plannedPayments.length > 0
      ? plannedPayments[plannedPayments.length - 1].date
      : input.repaymentStrategy.startDate;

  const actualPolicyLoan = calculateActualPolicyLoan({
    ...input.policyLoanInput,
    payments: plannedPayments,
    projectionEndDate,
  });

  const standardLoanComparison = buildStandardLoanAmortization({
    principal: input.repaymentStrategy.borrowedAmount,
    annualRate: input.repaymentStrategy.targetAnnualRate,
    termCount: input.repaymentStrategy.termCount,
    paymentFrequency: input.repaymentStrategy.paymentFrequency,
    startDate: input.repaymentStrategy.startDate,
  });

  const familySystemRecapture = calculateFamilySystemRecapture(
    {
      standardLoanResult: standardLoanComparison,
      actualPolicyLoanResult: actualPolicyLoan,
    },
    input.jurisdiction ?? "canada"
  );

  return {
    plannedPayments,
    actualPolicyLoan,
    standardLoanComparison,
    familySystemRecapture,
  };
}

// ============================================================
// SECTION 10: FAMILY SYSTEM RECAPTURE SUMMARY™
// ============================================================

type FamilySystemRecaptureInput = {
  standardLoanResult: StandardLoanResult;
  actualPolicyLoanResult: PolicyLoanResult;
};

/**
 * The ONLY differences between the former Canada and USA engines were these
 * narrative strings (IBC™/Nelson Nash™ language for Canada, generic family
 * financing language for USA). Both jurisdictions now share one engine;
 * `usaLoanEngine.ts` is a thin wrapper binding jurisdiction = "usa".
 */
export const JURISDICTION_NARRATIVES: Record<
  Jurisdiction,
  {
    financingMarginExplanation: string;
    commitmentEducationalNote: string;
    commitmentSignatureDisclaimer: string;
  }
> = {
  canada: {
    financingMarginExplanation:
      "This amount represents the additional financing margin the client intended to pay above the actual policy loan interest cost. In the spirit of the Infinite Banking Concept™, this may be redirected into premiums or policy funding within the family system, subject to product limits, underwriting, tax rules, and advisor guidance.",
    commitmentEducationalNote:
      "The client chose to repay this loan with a stronger financing discipline than the actual policy loan interest charge. The difference may represent an opportunity to recapture additional value inside the family system. This concept is often illustrated by Nelson Nash™ in Becoming Your Own Banker™ through the idea sometimes described as 'stealing the peas.'",
    commitmentSignatureDisclaimer:
      "This document is a planning tool only. Actual policy loan interest, repayment timing, premium limits, tax consequences, and policy values may differ. Clients should confirm all policy details with their advisor and the life company before acting.",
  },
  usa: {
    financingMarginExplanation:
      "This represents the additional financing margin the borrower intended to pay above the policy loan interest charge. Within a family financing strategy this margin may be redirected into additional premiums or policy funding so financial energy stays within the family system.",
    commitmentEducationalNote:
      "The borrower chose to repay this loan with a stronger financing discipline than the actual policy loan interest charge. The difference may represent an opportunity to recapture additional value inside the family system.",
    commitmentSignatureDisclaimer:
      "This document is a planning tool only. Actual policy loan interest, dividend treatment, and loan mechanics may vary between life insurance companies and policy forms. Loan timing, payment timing, and other variables may affect results. All charts, schedules, and projections assume the policy loan interest rate remains constant unless a stress test is specifically included. If the policy loan rate changes, the remaining schedule should be recalculated. These estimates are designed to encourage intentionality and disciplined loan repayment as part of your family financing strategy.",
  },
};

export function calculateFamilySystemRecapture(
  input: FamilySystemRecaptureInput,
  jurisdiction: Jurisdiction = "canada"
): FamilySystemRecaptureResult {
  const intendedPaymentPerPeriod = input.standardLoanResult.scheduledPayment;
  const intendedTotalPayments = input.standardLoanResult.totalPayments;
  const intendedTotalInterest = input.standardLoanResult.totalInterest;
  const actualPolicyLoanInterestCost =
    input.actualPolicyLoanResult.totalInterestAccruedToProjectionEnd;
  const rawMargin = intendedTotalInterest - actualPolicyLoanInterestCost;
  const additionalIntendedFinancingMargin = Math.max(0, rawMargin);

  return {
    intendedPaymentPerPeriod,
    intendedTotalPayments,
    intendedTotalInterest,
    actualPolicyLoanInterestCost,
    additionalIntendedFinancingMargin,
    suggestedExtraToRedirectToFamilySystemPremiums: additionalIntendedFinancingMargin,
    explanationText: JURISDICTION_NARRATIVES[jurisdiction].financingMarginExplanation,
  };
}

import { snapDisplayZero, isPaidOff } from './money';

// ============================================================
// SECTION 11: DASHBOARD SUMMARY
// ============================================================

// Money rounding + zero-snapping is centralized in src/lib/money.ts.
// Only values below ONE CENT are treated as zero.

export type DashboardSummary = {
  actualPolicyLoanBalance: number;
  accruedInterestNotCapitalized: number;
  totalIndebtedness: number;
  nextCapitalizationEstimate: number;
  targetPaymentPerPeriod: number;
  bankLoanTotalInterest: number;
  policyLoanTotalInterest: number;
  totalPaymentsApplied: number;
  additionalIntendedFinancingMargin: number;
  suggestedExtraToFamilySystemPremiums: number;
  hasLapseRiskWarning: boolean;
  effectivePolicyLoanRate: number;
  // Interest volume: interest as % of total payments
  policyLoanInterestVolume: number;
  standardLoanInterestVolume: number;
  standardLoanTotalPayments: number;
  // Early payoff
  policyLoanPayoffDate: string | null;
  standardLoanPayoffDate: string;
  standardLoanPaymentCount: number;
  policyLoanPaymentCount: number;
  earlyPayoffPeriods: number; // positive = policy loan pays off earlier
};

export function buildDashboardSummary(
  combined: CombinedScenarioResult
): DashboardSummary {
  const totalPaid = combined.actualPolicyLoan.totalPaymentsApplied;
  const totalInterest = combined.actualPolicyLoan.totalInterestAccruedToProjectionEnd;
  const effectivePolicyLoanRate = totalPaid > 0 ? totalInterest / totalPaid : 0;

  const standardTotalPaid = combined.standardLoanComparison.totalPayments;
  const standardTotalInterest = combined.standardLoanComparison.totalInterest;

  const policyLoanInterestVolume = totalPaid > 0 ? totalInterest / totalPaid : 0;
  const standardLoanInterestVolume = standardTotalPaid > 0 ? standardTotalInterest / standardTotalPaid : 0;

  const standardLoanPaymentCount = combined.standardLoanComparison.amortizationSchedule.length;
  // Count policy loan payments until principal reaches zero
  const paymentRows = combined.actualPolicyLoan.ledger.filter(
    r => r.eventType === 'payment' && r.paymentAmount > 0
  );
  let policyLoanPaymentCount = paymentRows.length;
  // Find the first payment where principal hit zero (payoff point)
  for (let i = 0; i < paymentRows.length; i++) {
    if (
      isPaidOff(paymentRows[i].endingPrincipal) &&
      isPaidOff(paymentRows[i].endingAccruedInterest)
    ) {
      policyLoanPaymentCount = i + 1;
      break;
    }
  }
  const earlyPayoffPeriods = standardLoanPaymentCount - policyLoanPaymentCount;

  return {
    actualPolicyLoanBalance: snapDisplayZero(combined.actualPolicyLoan.currentPrincipalBalance),
    accruedInterestNotCapitalized:
      snapDisplayZero(combined.actualPolicyLoan.currentAccruedInterestNotCapitalized),
    totalIndebtedness: snapDisplayZero(combined.actualPolicyLoan.currentTotalIndebtedness),
    nextCapitalizationEstimate: snapDisplayZero(combined.actualPolicyLoan.nextCapitalizationEstimate),
    targetPaymentPerPeriod: combined.familySystemRecapture.intendedPaymentPerPeriod,
    bankLoanTotalInterest: combined.standardLoanComparison.totalInterest,
    policyLoanTotalInterest:
      combined.actualPolicyLoan.totalInterestAccruedToProjectionEnd,
    totalPaymentsApplied: totalPaid,
    additionalIntendedFinancingMargin:
      combined.familySystemRecapture.additionalIntendedFinancingMargin,
    suggestedExtraToFamilySystemPremiums:
      combined.familySystemRecapture.suggestedExtraToRedirectToFamilySystemPremiums,
    hasLapseRiskWarning: combined.actualPolicyLoan.hasLapseRiskWarning,
    effectivePolicyLoanRate,
    policyLoanInterestVolume,
    standardLoanInterestVolume,
    standardLoanTotalPayments: standardTotalPaid,
    policyLoanPayoffDate: combined.actualPolicyLoan.estimatedPayoffDate,
    standardLoanPayoffDate: combined.standardLoanComparison.payoffDate,
    standardLoanPaymentCount,
    policyLoanPaymentCount,
    earlyPayoffPeriods,
  };
}

// ============================================================
// SECTION 12: COMMITMENT PAGE DATA
// ============================================================

export type CommitmentPageData = {
  clientName: string;
  advisorName: string;
  policyNumber?: string;
  loanPurpose: string;
  policyLoanEffectiveDate: string;
  policyAnniversaryDateForCurrentYearOrNext: string;
  loanAmount: number;
  policyLoanAnnualRate: number;
  chosenRepaymentRate: number;
  paymentFrequency: PaymentFrequency;
  targetPaymentAmount: number;
  targetNumberOfPayments: number;
  projectedFinalPaymentDate: string;
  actualPolicyLoanInterestCost: number;
  additionalIntendedFinancingMargin: number;
  suggestedExtraToFamilySystemPremiums: number;
  educationalNote: string;
  signatureDisclaimer: string;
};

export function buildCommitmentPageData(params: {
  clientName: string;
  advisorName: string;
  policyNumber?: string;
  loanPurpose: string;
  policyLoanInput: Omit<PolicyLoanInput, "payments" | "projectionEndDate">;
  repaymentStrategy: PlannedRepaymentStrategyInput;
  combined: CombinedScenarioResult;
  /** Selects jurisdiction-specific narrative text; math is identical. */
  jurisdiction?: Jurisdiction;
}): CommitmentPageData {
  return {
    clientName: params.clientName,
    advisorName: params.advisorName,
    policyNumber: params.policyNumber,
    loanPurpose: params.loanPurpose,
    policyLoanEffectiveDate: params.policyLoanInput.policyLoanEffectiveDate,
    policyAnniversaryDateForCurrentYearOrNext: getNextPolicyAnniversaryDate(
      params.policyLoanInput.policyLoanEffectiveDate,
      params.policyLoanInput.policyAnniversaryMonth,
      params.policyLoanInput.policyAnniversaryDay
    ),
    loanAmount: params.policyLoanInput.loanAmount,
    policyLoanAnnualRate: params.policyLoanInput.policyLoanAnnualRate,
    chosenRepaymentRate: params.repaymentStrategy.targetAnnualRate,
    paymentFrequency: params.repaymentStrategy.paymentFrequency,
    targetPaymentAmount:
      params.combined.familySystemRecapture.intendedPaymentPerPeriod,
    targetNumberOfPayments: params.repaymentStrategy.termCount,
    projectedFinalPaymentDate:
      params.combined.standardLoanComparison.payoffDate,
    actualPolicyLoanInterestCost:
      params.combined.familySystemRecapture.actualPolicyLoanInterestCost,
    additionalIntendedFinancingMargin:
      params.combined.familySystemRecapture.additionalIntendedFinancingMargin,
    suggestedExtraToFamilySystemPremiums:
      params.combined.familySystemRecapture.suggestedExtraToRedirectToFamilySystemPremiums,
    educationalNote:
      JURISDICTION_NARRATIVES[params.jurisdiction ?? "canada"].commitmentEducationalNote,
    signatureDisclaimer:
      JURISDICTION_NARRATIVES[params.jurisdiction ?? "canada"].commitmentSignatureDisclaimer,
  };
}

// ============================================================
// SECTION 13: VALIDATION
// ============================================================

export function validatePolicyLoanInput(input: PolicyLoanInput): string[] {
  const errors: string[] = [];
  if (input.loanAmount <= 0) errors.push("Loan amount must be greater than 0.");
  if (input.policyLoanAnnualRate < 0) errors.push("Policy loan rate cannot be negative.");
  if (!input.policyLoanEffectiveDate) errors.push("Policy Loan Effective Date is required.");
  if (input.policyAnniversaryMonth < 1 || input.policyAnniversaryMonth > 12) {
    errors.push("Policy anniversary month must be between 1 and 12.");
  }
  if (input.policyAnniversaryDay < 1 || input.policyAnniversaryDay > 31) {
    errors.push("Policy anniversary day must be between 1 and 31.");
  }
  input.payments.forEach((p) => {
    if (p.amount <= 0) errors.push(`Payment ${p.id} must be greater than 0.`);
    if (compareDateStr(p.date, input.policyLoanEffectiveDate) < 0) {
      errors.push(`Payment ${p.id} cannot be before the Policy Loan Effective Date.`);
    }
  });
  (input.rateChanges ?? []).forEach((rc) => {
    if (rc.annualRate < 0) errors.push(`Rate change ${rc.id} cannot be negative.`);
  });
  return errors;
}

export function validateStandardLoanInput(input: StandardLoanInput): string[] {
  const errors: string[] = [];
  if (input.principal <= 0) errors.push("Principal must be greater than 0.");
  if (input.annualRate < 0) errors.push("Annual rate cannot be negative.");
  if (input.termCount <= 0) errors.push("Term count must be greater than 0.");
  if (!input.startDate) errors.push("Start date is required.");
  return errors;
}
